import React, { useState, useEffect, useMemo } from 'react';
import bedrockService from '../services/bedrockService';
import ReactMarkdown from 'react-markdown';

const SECAnalysisJobsTable = ({ category = 'SEC Filings Analysis' }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [editingJobId, setEditingJobId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [deletingJobId, setDeletingJobId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);

  // Search, filter, and sort state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    fetchJobs();
  }, [category]);

  const fetchJobs = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await bedrockService.getAnalysisJobsByCategory(category, 100);
      if (response.success) {
        setJobs(response.jobs || []);
      } else {
        setError('Failed to fetch analysis jobs');
      }
    } catch (err) {
      console.error('Error fetching analysis jobs:', err);
      setError(err.message || 'Failed to fetch analysis jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleViewSummary = (job) => {
    setSelectedJob(job);
    setShowSummaryModal(true);
  };

  const handleEditName = (job) => {
    setEditingJobId(job.job_id);
    setEditingName(job.friendly_name || job.original_filename || '');
  };

  const handleSaveName = async (jobId) => {
    try {
      await bedrockService.updateJobFriendlyName(jobId, editingName);
      
      // Update local state
      setJobs(prevJobs =>
        prevJobs.map(job =>
          job.job_id === jobId ? { ...job, friendly_name: editingName } : job
        )
      );
      
      setEditingJobId(null);
      setEditingName('');
    } catch (err) {
      console.error('Error updating friendly name:', err);
      alert(`Failed to update name: ${err.message}`);
    }
  };

  const handleCancelEdit = () => {
    setEditingJobId(null);
    setEditingName('');
  };

  const handleDeleteClick = (job) => {
    setJobToDelete(job);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!jobToDelete) return;
    
    setDeletingJobId(jobToDelete.job_id);
    try {
      await bedrockService.deleteJob(jobToDelete.job_id);
      
      // Remove from local state
      setJobs(prevJobs => prevJobs.filter(job => job.job_id !== jobToDelete.job_id));
      
      setShowDeleteConfirm(false);
      setJobToDelete(null);
    } catch (err) {
      console.error('Error deleting job:', err);
      alert(`Failed to delete job: ${err.message}`);
    } finally {
      setDeletingJobId(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setJobToDelete(null);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-success';
      case 'failed':
        return 'bg-danger';
      case 'processing':
      case 'summarizing':
      case 'analyzing_filename':
      case 'filename_generated':
      case 'determining_category':
        return 'bg-warning text-dark';
      case 'pending':
        return 'bg-secondary';
      default:
        return 'bg-secondary';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return 'bi-check-circle';
      case 'failed':
        return 'bi-x-circle';
      case 'processing':
      case 'summarizing':
      case 'analyzing_filename':
      case 'filename_generated':
      case 'determining_category':
        return 'bi-hourglass-split';
      case 'pending':
        return 'bi-clock';
      default:
        return 'bi-question-circle';
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'N/A';
    try {
      const date = new Date(isoString);
      return date.toLocaleString();
    } catch (err) {
      return isoString;
    }
  };

  const getDisplayName = (job) => {
    return job.friendly_name || job.original_filename || job.file_id || 'Untitled';
  };

  const copySummaryToClipboard = () => {
    if (selectedJob && selectedJob.summary) {
      navigator.clipboard.writeText(selectedJob.summary);
      alert('Summary copied to clipboard!');
    }
  };

  // Get unique clients for filter dropdown (extract from pipe-separated strings)
  const uniqueClients = useMemo(() => {
    const allClients = new Set();
    jobs.forEach(job => {
      if (job.client_name) {
        job.client_name.split('|').forEach(client => {
          const trimmed = client.trim();
          if (trimmed) allClients.add(trimmed);
        });
      }
    });
    return [...allClients].sort();
  }, [jobs]);

  // Get unique statuses for filter dropdown
  const uniqueStatuses = useMemo(() => {
    const statuses = [...new Set(jobs.map(job => job.status).filter(Boolean))];
    return statuses.sort();
  }, [jobs]);

  // Filter and sort jobs
  const filteredAndSortedJobs = useMemo(() => {
    let result = [...jobs];

    // Apply search filter
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      result = result.filter(job => {
        const displayName = getDisplayName(job).toLowerCase();
        const clientName = (job.client_name || '').toLowerCase();
        const jobId = (job.job_id || '').toLowerCase();
        return displayName.includes(lowerSearch) || 
               clientName.includes(lowerSearch) ||
               jobId.includes(lowerSearch);
      });
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      result = result.filter(job => job.status === statusFilter);
    }

    // Apply client filter (check if client_name contains the selected client)
    if (clientFilter !== 'all') {
      result = result.filter(job => {
        if (!job.client_name) return false;
        const clients = job.client_name.split('|').map(c => c.trim());
        return clients.includes(clientFilter);
      });
    }

    // Apply sorting
    result.sort((a, b) => {
      let aVal, bVal;

      switch (sortField) {
        case 'name':
          aVal = getDisplayName(a).toLowerCase();
          bVal = getDisplayName(b).toLowerCase();
          break;
        case 'client_name':
          aVal = (a.client_name || '').toLowerCase();
          bVal = (b.client_name || '').toLowerCase();
          break;
        case 'status':
          aVal = a.status || '';
          bVal = b.status || '';
          break;
        case 'created_at':
          aVal = a.created_at || '';
          bVal = b.created_at || '';
          break;
        case 'updated_at':
          aVal = a.updated_at || '';
          bVal = b.updated_at || '';
          break;
        default:
          aVal = a.created_at || '';
          bVal = b.created_at || '';
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [jobs, searchTerm, statusFilter, clientFilter, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return 'bi-chevron-expand';
    return sortDirection === 'asc' ? 'bi-chevron-up' : 'bi-chevron-down';
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setClientFilter('all');
    setSortField('created_at');
    setSortDirection('desc');
  };

  const hasActiveFilters = searchTerm || statusFilter !== 'all' || clientFilter !== 'all';

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4>SEC Filings Analysis Jobs</h4>
        <button
          className="btn btn-sm btn-outline-secondary"
          onClick={fetchJobs}
          disabled={loading}
        >
          <i className={`bi bi-arrow-clockwise ${loading ? 'spin' : ''} me-1`}></i>
          Refresh
        </button>
      </div>

      {/* Search, Filter, and Sort Controls */}
      <div className="card mb-3">
        <div className="card-body py-2">
          <div className="row g-2 align-items-center">
            {/* Search */}
            <div className="col-md-4">
              <div className="input-group input-group-sm">
                <span className="input-group-text">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by name, client, or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => setSearchTerm('')}
                    title="Clear search"
                  >
                    <i className="bi bi-x"></i>
                  </button>
                )}
              </div>
            </div>

            {/* Status Filter */}
            <div className="col-md-2">
              <select
                className="form-select form-select-sm"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Statuses</option>
                {uniqueStatuses.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>

            {/* Client Filter */}
            <div className="col-md-3">
              <select
                className="form-select form-select-sm"
                value={clientFilter}
                onChange={(e) => setClientFilter(e.target.value)}
              >
                <option value="all">All Clients</option>
                {uniqueClients.map(client => (
                  <option key={client} value={client}>{client}</option>
                ))}
              </select>
            </div>

            {/* Results Count and Clear */}
            <div className="col-md-3 text-end">
              <small className="text-muted me-2">
                {filteredAndSortedJobs.length} of {jobs.length} jobs
              </small>
              {hasActiveFilters && (
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={clearFilters}
                  title="Clear all filters"
                >
                  <i className="bi bi-x-circle me-1"></i>
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {loading && jobs.length === 0 ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Loading analysis jobs...</p>
        </div>
      ) : error ? (
        <div className="alert alert-danger" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
          <button className="btn btn-sm btn-outline-danger ms-3" onClick={fetchJobs}>
            Retry
          </button>
        </div>
      ) : jobs.length === 0 ? (
        <div className="text-center text-muted py-5">
          <i className="bi bi-inbox display-4 mb-3"></i>
          <p>No analysis jobs found for category: {category}</p>
        </div>
      ) : filteredAndSortedJobs.length === 0 ? (
        <div className="text-center text-muted py-5">
          <i className="bi bi-search display-4 mb-3"></i>
          <p>No jobs match your filters</p>
          <button className="btn btn-sm btn-outline-secondary" onClick={clearFilters}>
            Clear Filters
          </button>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover">
            <thead className="table-light">
              <tr>
                <th 
                  style={{ cursor: 'pointer' }} 
                  onClick={() => handleSort('name')}
                  className="user-select-none"
                >
                  Name / File <i className={`bi ${getSortIcon('name')}`}></i>
                </th>
                <th 
                  style={{ cursor: 'pointer' }} 
                  onClick={() => handleSort('client_name')}
                  className="user-select-none"
                >
                  Client <i className={`bi ${getSortIcon('client_name')}`}></i>
                </th>
                <th 
                  style={{ cursor: 'pointer' }} 
                  onClick={() => handleSort('status')}
                  className="user-select-none"
                >
                  Status <i className={`bi ${getSortIcon('status')}`}></i>
                </th>
                <th 
                  style={{ cursor: 'pointer' }} 
                  onClick={() => handleSort('created_at')}
                  className="user-select-none"
                >
                  Created <i className={`bi ${getSortIcon('created_at')}`}></i>
                </th>
                <th 
                  style={{ cursor: 'pointer' }} 
                  onClick={() => handleSort('updated_at')}
                  className="user-select-none"
                >
                  Updated <i className={`bi ${getSortIcon('updated_at')}`}></i>
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedJobs.map((job) => (
                <tr key={job.job_id}>
                  <td>
                    {editingJobId === job.job_id ? (
                      <div className="input-group input-group-sm">
                        <input
                          type="text"
                          className="form-control"
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleSaveName(job.job_id);
                            }
                          }}
                          autoFocus
                        />
                        <button
                          className="btn btn-outline-success"
                          onClick={() => handleSaveName(job.job_id)}
                          title="Save"
                        >
                          <i className="bi bi-check"></i>
                        </button>
                        <button
                          className="btn btn-outline-secondary"
                          onClick={handleCancelEdit}
                          title="Cancel"
                        >
                          <i className="bi bi-x"></i>
                        </button>
                      </div>
                    ) : (
                      <div>
                        <strong>{getDisplayName(job)}</strong>
                        <button
                          className="btn btn-sm btn-link p-0 ms-2"
                          onClick={() => handleEditName(job)}
                          title="Edit name"
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        {job.friendly_name && (
                          <div>
                            <small className="text-muted">{job.original_filename}</small>
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td>
                    {job.client_name ? (
                      <div>
                        {job.client_name.split('|').map((client, idx) => (
                          <div key={idx}>
                            <small>{client.trim()}</small>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <small>-</small>
                    )}
                  </td>
                  <td>
                    <span className={`badge ${getStatusBadgeClass(job.status)}`}>
                      <i className={`bi ${getStatusIcon(job.status)} me-1`}></i>
                      {job.status}
                    </span>
                    {job.status === 'failed' && job.error && (
                      <div className="mt-1">
                        <small className="text-danger" title={job.error}>
                          <i className="bi bi-exclamation-triangle"></i> Error
                        </small>
                      </div>
                    )}
                  </td>
                  <td>
                    <small>{formatDate(job.created_at)}</small>
                  </td>
                  <td>
                    <small>{formatDate(job.updated_at)}</small>
                  </td>
                  <td>
                    <div className="btn-group btn-group-sm" role="group">
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={() => handleViewSummary(job)}
                        disabled={!job.summary || job.status !== 'completed'}
                        title="View Summary"
                      >
                        <i className="bi bi-eye"></i>
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-danger"
                        onClick={() => handleDeleteClick(job)}
                        disabled={deletingJobId === job.job_id}
                        title="Delete Job"
                      >
                        {deletingJobId === job.job_id ? (
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        ) : (
                          <i className="bi bi-trash"></i>
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Summary Modal */}
      {showSummaryModal && selectedJob && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-file-text me-2"></i>
                  {getDisplayName(selectedJob)}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowSummaryModal(false);
                    setSelectedJob(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6>Analysis Summary</h6>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={copySummaryToClipboard}
                    >
                      <i className="bi bi-clipboard me-1"></i>
                      Copy
                    </button>
                  </div>
                  <div className="bg-light p-3 rounded" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    {selectedJob.summary ? (
                      <ReactMarkdown>{selectedJob.summary}</ReactMarkdown>
                    ) : (
                      <p className="text-muted">No summary available</p>
                    )}
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <small className="text-muted">Client:</small> {selectedJob.client_name || '-'}
                  </div>
                  <div className="col-md-6">
                    <small className="text-muted">Created:</small> {formatDate(selectedJob.created_at)}
                  </div>
                </div>
                {selectedJob.llm_details && (
                  <div className="mt-2">
                    <small className="text-muted">
                      Model: {selectedJob.llm_details.model_id || 'N/A'}
                    </small>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowSummaryModal(false);
                    setSelectedJob(null);
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
      `}</style>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && jobToDelete && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-exclamation-triangle text-danger me-2"></i>
                  Confirm Delete
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCancelDelete}
                ></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this analysis job?</p>
                <div className="alert alert-light">
                  <strong>{getDisplayName(jobToDelete)}</strong>
                  {jobToDelete.client_name && (
                    <div><small className="text-muted">Client: {jobToDelete.client_name}</small></div>
                  )}
                </div>
                <p className="text-muted small mb-0">
                  <i className="bi bi-info-circle me-1"></i>
                  This action cannot be undone.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCancelDelete}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleConfirmDelete}
                  disabled={deletingJobId === jobToDelete.job_id}
                >
                  {deletingJobId === jobToDelete.job_id ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-trash me-1"></i>
                      Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SECAnalysisJobsTable;
